import { useState, useRef, useEffect } from "react";
import { Link } from "react-router-dom";
import { categories } from "@/mocks/categories";

export default function Navbar() {
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [mobileCatOpen, setMobileCatOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const mobileRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setDropdownOpen(false);
      }
      if (mobileRef.current && !mobileRef.current.contains(e.target as Node)) {
        const target = e.target as HTMLElement;
        if (!target.closest(".mobile-menu-trigger")) {
          setMobileOpen(false);
        }
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const closeMobile = () => setMobileOpen(false);

  const textColor = scrolled ? "text-foreground-900" : "text-background-50";
  const textMuted = scrolled ? "text-foreground-700" : "text-background-200/90";
  const textHover = scrolled ? "hover:text-primary-500" : "hover:text-primary-300";
  const borderColor = scrolled ? "border-background-300" : "border-background-200/40";
  const dropdownBg = scrolled ? "bg-background-50" : "bg-background-50/95 backdrop-blur-md";

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled
          ? "bg-background-50/95 backdrop-blur-md border-b border-background-200/70"
          : "bg-transparent"
      }`}
    >
      <div className="flex items-center justify-between h-16 md:h-16 px-4 md:px-8">
        <Link to="/" className="flex items-center gap-2">
          <span className={`font-heading text-xl md:text-2xl font-bold whitespace-nowrap transition-colors duration-300 ${textColor}`}>
            CSSBuy<span className="text-primary-400">Spreadsheet</span>
          </span>
        </Link>

        <div className="hidden md:flex items-center gap-6">
          <Link to="/" className={`text-sm transition-colors whitespace-nowrap ${textMuted} ${textHover}`}>
            Início
          </Link>
          <Link to="/artigo/cssbuy-sheet-guia-completo" className={`text-sm transition-colors whitespace-nowrap ${textMuted} ${textHover}`}>
            Guia de Compras
          </Link>
          <Link to="/artigo/cssbuy-sheet-guia-completo" className={`text-sm transition-colors whitespace-nowrap ${textMuted} ${textHover}`}>
            Artigos
          </Link>
          <a href="#faq" className={`text-sm transition-colors whitespace-nowrap ${textMuted} ${textHover}`}>
            FAQ
          </a>

          <div ref={dropdownRef} className="relative">
            <button
              onClick={() => setDropdownOpen(!dropdownOpen)}
              className={`flex items-center gap-2 px-4 py-2 rounded-full border text-sm transition-colors whitespace-nowrap cursor-pointer ${
                scrolled
                  ? "border-background-300 bg-background-50 text-foreground-700 hover:border-primary-300"
                  : "border-background-200/40 text-background-50 hover:border-background-200/80"
              }`}
            >
              <span>Categorias</span>
              <i className={`ri-arrow-down-s-line transition-transform duration-200 ${dropdownOpen ? "rotate-180" : ""}`}></i>
            </button>

            {dropdownOpen && (
              <div className={`absolute top-full mt-2 right-0 w-64 border rounded-xl overflow-hidden shadow-lg max-h-80 overflow-y-auto z-50 ${dropdownBg} ${scrolled ? "border-background-200" : "border-background-200/40"}`}>
                {categories.map((cat) => (
                  <a
                    key={cat.id}
                    href={cat.url}
                    target="_blank"
                    rel="noopener nofollow"
                    className="flex items-center gap-3 px-4 py-3 text-sm text-foreground-700 hover:bg-primary-50 hover:text-primary-600 transition-colors cursor-pointer"
                  >
                    <i className={`${cat.icon} text-base w-5 h-5 flex items-center justify-center`}></i>
                    <span>{cat.namePt}</span>
                  </a>
                ))}
              </div>
            )}
          </div>

          <a
            href="https://kaspreadsheet.com"
            target="_blank"
            rel="noopener nofollow"
            className="px-5 py-2.5 bg-primary-500 text-background-50 rounded-full text-sm font-semibold hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
          >
            Buy It Now
          </a>
        </div>

        <button
          onClick={() => setMobileOpen(true)}
          className={`md:hidden w-9 h-9 flex items-center justify-center cursor-pointer mobile-menu-trigger transition-colors duration-300 ${textColor}`}
        >
          <i className="ri-menu-line text-2xl"></i>
        </button>
      </div>

      {mobileOpen && (
        <div className="fixed inset-0 z-[60] md:hidden">
          <div
            className="absolute inset-0 bg-foreground-900/50 backdrop-blur-sm"
            onClick={closeMobile}
          ></div>
          <div
            ref={mobileRef}
            className="absolute top-0 right-0 w-80 max-w-[85vw] h-full bg-background-50 overflow-y-auto"
          >
            <div className="flex items-center justify-between px-5 py-4 border-b border-background-200">
              <span className="font-heading text-lg font-bold text-foreground-900">
                CSSBuy<span className="text-primary-500">Spreadsheet</span>
              </span>
              <button
                onClick={closeMobile}
                className="w-9 h-9 flex items-center justify-center rounded-full hover:bg-background-100 cursor-pointer"
              >
                <i className="ri-close-line text-xl text-foreground-700"></i>
              </button>
            </div>

            <div className="p-5 space-y-1">
              <Link
                to="/"
                onClick={closeMobile}
                className="flex items-center gap-3 px-4 py-3 rounded-xl text-sm text-foreground-700 hover:bg-background-100 transition-colors cursor-pointer"
              >
                <i className="ri-home-4-line text-lg w-5 h-5 flex items-center justify-center text-foreground-500"></i>
                <span>Início</span>
              </Link>

              <Link
                to="/artigo/cssbuy-sheet-guia-completo"
                onClick={closeMobile}
                className="flex items-center gap-3 px-4 py-3 rounded-xl text-sm text-foreground-700 hover:bg-background-100 transition-colors cursor-pointer"
              >
                <i className="ri-book-open-line text-lg w-5 h-5 flex items-center justify-center text-foreground-500"></i>
                <span>Guia de Compras</span>
              </Link>

              <Link
                to="/artigo/cssbuy-sheet-guia-completo"
                onClick={closeMobile}
                className="flex items-center gap-3 px-4 py-3 rounded-xl text-sm text-foreground-700 hover:bg-background-100 transition-colors cursor-pointer"
              >
                <i className="ri-article-line text-lg w-5 h-5 flex items-center justify-center text-foreground-500"></i>
                <span>Artigos SEO</span>
              </Link>

              <a
                href="#faq"
                onClick={closeMobile}
                className="flex items-center gap-3 px-4 py-3 rounded-xl text-sm text-foreground-700 hover:bg-background-100 transition-colors cursor-pointer"
              >
                <i className="ri-question-line text-lg w-5 h-5 flex items-center justify-center text-foreground-500"></i>
                <span>FAQ</span>
              </a>

              <div className="pt-2">
                <button
                  onClick={() => setMobileCatOpen(!mobileCatOpen)}
                  className="flex items-center justify-between w-full px-4 py-3 rounded-xl text-sm text-foreground-700 hover:bg-background-100 transition-colors cursor-pointer"
                >
                  <span className="flex items-center gap-3">
                    <i className="ri-grid-line text-lg w-5 h-5 flex items-center justify-center text-foreground-500"></i>
                    <span>Categorias</span>
                  </span>
                  <i className={`ri-arrow-down-s-line transition-transform duration-200 ${mobileCatOpen ? "rotate-180" : ""}`}></i>
                </button>

                {mobileCatOpen && (
                  <div className="ml-4 mt-1 space-y-0.5 border-l-2 border-background-200 pl-3">
                    {categories.map((cat) => (
                      <a
                        key={cat.id}
                        href={cat.url}
                        target="_blank"
                        rel="noopener nofollow"
                        onClick={closeMobile}
                        className="flex items-center gap-3 px-4 py-2.5 rounded-lg text-sm text-foreground-600 hover:bg-primary-50 hover:text-primary-600 transition-colors cursor-pointer"
                      >
                        <i className={`${cat.icon} text-sm w-5 h-5 flex items-center justify-center`}></i>
                        <span>{cat.namePt}</span>
                      </a>
                    ))}
                  </div>
                )}
              </div>

              <div className="pt-4">
                <a
                  href="https://kaspreadsheet.com"
                  target="_blank"
                  rel="noopener nofollow"
                  onClick={closeMobile}
                  className="flex items-center justify-center gap-2 w-full px-5 py-3 bg-primary-500 text-background-50 rounded-full text-sm font-semibold hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
                >
                  Buy It Now
                  <i className="ri-arrow-right-up-line"></i>
                </a>
              </div>
            </div>
          </div>
        </div>
      )}
    </nav>
  );
}